import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Check, Star, ArrowRight, CaretDown, Camera, Heart, BuildingOffice, Confetti } from "@phosphor-icons/react";
import Seo from "@/components/Seo";

const SITE_URL = "https://pixora38.fr";

const formulas = [
  {
    id: "start",
    name: "Pixora Start",
    price: 149,
    description: "Formule 100 % digitale avec galerie photos en ligne, 20 cadres personnalisables, accessoires photo et flash LED professionnel.",
  },
  {
    id: "signature",
    name: "Pixora Signature",
    price: 199,
    description: "La formule équilibrée : galerie digitale + 50 impressions haute qualité incluses, parfaite pour anniversaires et soirées privées.",
    popular: true,
  },
  {
    id: "prestige",
    name: "Pixora Prestige",
    price: 279,
    description: "L'expérience premium avec 150 impressions haute qualité et option livre d'or pour vos événements marquants.",
  },
  {
    id: "wedding",
    name: "Pixora Wedding",
    price: 389,
    description: "La formule mariage complète : 350 impressions, livre d'or personnalisé et props premium spécial mariage.",
    highlight: true,
  },
];

const occasions = [
  { icon: Heart, title: "Mariages à Bourgoin-Jallieu", text: "Props mariage, livre d'or personnalisé et impressions instantanées pour immortaliser le plus beau jour de votre vie." },
  { icon: Confetti, title: "Anniversaires & soirées", text: "Un photobooth festif pour anniversaires surprises, EVJF, Noël, baptêmes et toutes vos soirées entre amis." },
  { icon: BuildingOffice, title: "Événements professionnels", text: "Séminaires, lancements de produits et soirées d'entreprise — un animation originale qui fédère vos collaborateurs." },
];

const faqItems = [
  {
    q: "Quel est le prix de la location d'un photobooth à Bourgoin-Jallieu ?",
    a: "Nos formules démarrent à 149 € pour la formule Start (digitale) et vont jusqu'à 389 € pour la formule Wedding complète. L'installation et la récupération sont incluses jusqu'à 20 km de Bourgoin-Jallieu."
  },
  {
    q: "Est-ce que l'installation du photobooth est incluse ?",
    a: "Oui, toutes nos formules incluent l'installation, le réglage et la récupération du photobooth. Nous arrivons en avance pour que tout soit prêt dès le début de votre événement."
  },
  {
    q: "Dans quelles communes autour de Bourgoin-Jallieu intervenez-vous ?",
    a: "Nous intervenons dans tout le Nord-Isère et l'Est Lyonnais : Bourgoin-Jallieu, L'Isle-d'Abeau, La Tour-du-Pin, Villefontaine, Crémieu, Morestel, La Verpillière, Meyzieu, Décines-Charpieu, Chassieu, Pont-de-Chéruy et Lyon."
  },
  {
    q: "Combien de temps dure la location du photobooth ?",
    a: "Nos formules sont pensées pour couvrir toute la durée de votre événement, généralement 4 à 6 heures. Nous pouvons adapter la durée selon vos besoins."
  },
  {
    q: "Peut-on personnaliser les cadres photo et le livre d'or ?",
    a: "Oui, 20 cadres photo sont personnalisables selon votre thème (prénoms, date, logo). La formule Wedding inclut un livre d'or entièrement personnalisé aux couleurs de votre mariage."
  },
  {
    q: "Comment réserver un photobooth à Bourgoin-Jallieu ?",
    a: "Vous pouvez réserver en ligne via notre calendrier de disponibilités ou nous contacter directement par téléphone ou email. Nous recommandons de réserver au moins 1 mois à l'avance pour les mariages."
  },
];

const galleryImages = [
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pixora-moments/artifacts/g4m22ysq_ChatGPT%20Image%2024%20sept.%202026%2C%2011_08_13.png",
    alt: "Photobooth Pixora installé lors d'un événement à Bourgoin-Jallieu",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pixora-moments/artifacts/ja3oahjr_IMG20260919135240.webp",
    alt: "Props et accessoires photobooth pour mariage à Bourgoin-Jallieu",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pixora-moments/artifacts/ouwfrswm_IMG20260919135303.webp",
    alt: "Livre d'or personnalisé mariage Pixora Nord-Isère",
  },
  {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/job_pixora-moments/artifacts/8dz7az8x_IMG20260919135535.webp",
    alt: "Panneaux et props originaux photobooth Nord-Isère",
  },
];

export default function PhotoboothBourgoinJallieu() {
  const [openFaq, setOpenFaq] = useState(0);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${SITE_URL}/photobooth-bourgoin-jallieu/#service`,
        "name": "Location de photobooth à Bourgoin-Jallieu",
        "serviceType": "Location de borne photo",
        "provider": { "@id": `${SITE_URL}/#business` },
        "areaServed": {
          "@type": "City",
          "name": "Bourgoin-Jallieu",
          "containedInPlace": { "@type": "AdministrativeArea", "name": "Isère" }
        },
        "description": "Location de photobooth premium à Bourgoin-Jallieu et dans tout le Nord-Isère pour mariages, anniversaires, soirées privées et événements professionnels. Installation et récupération incluses.",
        "offers": formulas.map(f => ({
          "@type": "Offer",
          "name": f.name,
          "price": String(f.price),
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "url": `${SITE_URL}/reservation`,
        })),
      },
      {
        "@type": "FAQPage",
        "@id": `${SITE_URL}/photobooth-bourgoin-jallieu/#faq`,
        "mainEntity": faqItems.map(item => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": { "@type": "Answer", "text": item.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${SITE_URL}/` },
          { "@type": "ListItem", "position": 2, "name": "Photobooth Bourgoin-Jallieu", "item": `${SITE_URL}/photobooth-bourgoin-jallieu` },
        ],
      },
    ],
  };

  return (
    <div className="pt-24 md:pt-32 lg:pt-36" data-testid="photobooth-bourgoin-jallieu-page">
      <Seo
        title="Photobooth Bourgoin-Jallieu : Location Borne Photo Mariage & Événements | Pixora"
        description="Vous cherchez un photobooth à Bourgoin-Jallieu ? Pixora propose la location de borne photo premium pour mariages, anniversaires et événements. Installation incluse en Nord-Isère."
        path="/photobooth-bourgoin-jallieu"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="py-16 md:py-24 bokeh-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full bg-[#C9A962]/10 border border-[#C9A962]/30">
              <MapPin size={16} className="text-[#C9A962]" weight="fill" />
              <span className="text-sm text-[#C9A962] uppercase tracking-wider">Bourgoin-Jallieu · Nord-Isère</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-6">
              Location de <span className="gradient-text">Photobooth</span>
              <span className="block text-3xl sm:text-4xl md:text-5xl mt-2">à Bourgoin-Jallieu</span>
            </h1>
            <p className="text-lg md:text-xl text-[#D8CCA8] max-w-3xl mx-auto leading-relaxed">
              Pixora est votre spécialiste local de la <strong className="text-white">location de borne photo premium à Bourgoin-Jallieu</strong>. Pour vos mariages, anniversaires, baptêmes ou soirées d'entreprise, nous installons un photobooth haut de gamme dans tout le Nord-Isère — installation et récupération incluses.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/reservation" data-testid="bourgoin-cta-reserve">
                <button className="btn-primary flex items-center justify-center gap-2 text-base px-7 py-4">
                  <Camera size={20} weight="bold" /> Réserver maintenant
                </button>
              </Link>
              <Link to="/contact" data-testid="bourgoin-cta-quote">
                <button className="btn-secondary flex items-center justify-center gap-2 text-base px-7 py-4">
                  Demander un devis <ArrowRight size={18} />
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRESENTATION */}
      <section className="py-14 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
              Un service de <span className="gradient-text">photobooth</span> pensé pour Bourgoin-Jallieu
            </h2>
            <div className="space-y-5 text-[#D8CCA8] text-base md:text-lg leading-relaxed">
              <p>
                Basé au cœur du Nord-Isère, Pixora propose la <strong className="text-white">location d'un photobooth premium à Bourgoin-Jallieu</strong> et dans tout l'Est Lyonnais. Notre borne photo nouvelle génération capture vos invités avec un rendu professionnel, imprime leurs souvenirs en quelques secondes et alimente une galerie digitale sécurisée accessible après l'événement.
              </p>
              <p>
                Nous nous occupons de tout : livraison du photobooth, installation sur le lieu de votre choix à Bourgoin-Jallieu, réglages (éclairage, hauteur, fond), accompagnement de vos invités puis récupération en fin de soirée. Vous profitez de votre événement, nous nous chargeons du reste.
              </p>
              <p>
                Chaque formule inclut un flash LED professionnel, des accessoires photo originaux et des cadres personnalisables à l'image de votre thème. Avec Pixora, vous bénéficiez d'un service local, réactif et clé en main.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OCCASIONS */}
      <section className="py-14 bokeh-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Pour tous vos <span className="gradient-text">événements</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {occasions.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl bg-[#0A0A0A] border border-[#C9A962]/20 p-6 hover:border-[#C9A962]/50 transition-colors"
                  data-testid={`occasion-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C9A962]/10 flex items-center justify-center border border-[#C9A962]/30 mb-4">
                    <Icon size={22} className="text-[#C9A962]" weight="fill" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-[#B8A88A] text-sm leading-relaxed">{item.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORMULES */}
      <section className="py-14 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Nos <span className="gradient-text">formules</span> à Bourgoin-Jallieu
            </h2>
            <p className="text-[#B8A88A] mt-3">Quatre formules claires et transparentes, de 149 € à 389 €.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {formulas.map((f, i) => (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`relative flex flex-col rounded-2xl p-6 bg-[#0A0A0A] ${
                  f.popular ? "border-2 border-[#C9A962] shadow-[0_0_30px_rgba(201,169,98,0.2)]" :
                  f.highlight ? "border-2 border-[#C9A962]/60" :
                  "border border-[#C9A962]/20"
                }`}
                data-testid={`formula-card-${f.id}`}
              >
                {f.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="flex items-center gap-1 bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black text-xs font-bold px-3 py-1 rounded-full">
                      <Star size={12} weight="fill" /> Populaire
                    </span>
                  </div>
                )}
                <h3 className="text-lg font-bold text-white mb-1">{f.name}</h3>
                <p className="text-3xl font-extrabold gradient-text my-2">{f.price}€</p>
                <p className="text-[#B8A88A] text-sm flex-1">{f.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/tarifs-particuliers" data-testid="link-full-pricing">
              <button className="btn-secondary flex items-center gap-2 mx-auto">
                Voir le détail des formules <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-14 bokeh-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Nos <span className="gradient-text">photobooths</span> en prestation
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="aspect-square overflow-hidden rounded-xl border border-[#C9A962]/20"
                data-testid={`bourgoin-gallery-${i}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ZONE */}
      <section className="py-14 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
              Zone d'<span className="gradient-text">intervention</span>
            </h2>
            <p className="text-[#D8CCA8] mb-6 leading-relaxed">
              Pixora intervient à <strong className="text-white">Bourgoin-Jallieu</strong> et dans toutes les communes du Nord-Isère et de l'Est Lyonnais. La livraison et la récupération sont incluses jusqu'à 20 km de Bourgoin-Jallieu ; au-delà, un forfait kilométrique transparent s'applique (0,50 € / km × 4 trajets).
            </p>
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-[#B8A88A] text-sm" data-testid="zone-list">
              {["Bourgoin-Jallieu", "L'Isle-d'Abeau", "La Tour-du-Pin", "Villefontaine", "Crémieu", "Morestel", "La Verpillière", "Meyzieu", "Décines-Charpieu", "Chassieu", "Pont-de-Chéruy", "Lyon"].map(city => (
                <li key={city} className="flex items-center gap-2"><Check size={14} className="text-[#C9A962]" />{city}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bokeh-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Questions <span className="gradient-text">fréquentes</span>
            </h2>
            <p className="text-[#B8A88A] mt-3">Tout ce que vous devez savoir sur la location d'un photobooth à Bourgoin-Jallieu.</p>
          </motion.div>

          <div className="space-y-3" data-testid="faq-list">
            {faqItems.map((item, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="rounded-xl bg-[#0A0A0A] border border-[#C9A962]/20 overflow-hidden" data-testid={`faq-item-${i}`}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 text-left p-5 hover:bg-[#C9A962]/5 transition-colors"
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={open}
                  >
                    <h3 className="text-base md:text-lg font-semibold text-white">{item.q}</h3>
                    <CaretDown size={18} className={`text-[#C9A962] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 text-[#B8A88A] leading-relaxed text-sm md:text-base border-t border-[#C9A962]/10 pt-4">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 bokeh-strong">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-5">
              Prêt à <span className="gradient-text">réserver</span> votre photobooth ?
            </h2>
            <p className="text-[#D8CCA8] mb-7">
              Vérifiez les disponibilités en direct ou demandez un devis gratuit pour votre événement à Bourgoin-Jallieu.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/reservation" data-testid="bourgoin-final-reserve">
                <button className="btn-primary">Réserver maintenant</button>
              </Link>
              <Link to="/contact" data-testid="bourgoin-final-quote">
                <button className="btn-secondary">Demander un devis</button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
